<template>
  <div v-if="!!progress" class="order-progress">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
      <div class="text-sm shipment-info">
        <i class="fa-solid fa-truck me-2"></i>
        <span>{{ $t('key_orderProgress_carrier') }}:</span>
        <strong class="ms-1 me-2">{{ courier }}</strong>
        <span>· {{ $t('key_orderProgress_tracking') }}</span>
        <strong class="ms-1">{{ trackingNumbers }}</strong>
      </div>

      <a
        v-if="trackingUrls.length === 1"
        class="btn btn-outline-primary mb-0"
        :href="trackingUrls[0]"
        target="_blank"
        rel="noopener noreferrer">
        {{ trackLabel }}
        <i class="fa-solid fa-arrow-up-right-from-square ms-2"></i>
      </a>
      <button
        v-else-if="trackingUrls.length > 1"
        type="button"
        class="btn btn-outline-primary mb-0"
        data-bs-toggle="modal"
        data-bs-target="#orderProgressTrackingModal">
        {{ trackLabel }}
        <i class="fa-solid fa-arrow-up-right-from-square ms-2"></i>
      </button>
    </div>

    <div class="milestones mt-4">
      <div
        v-for="(milestone, index) in progress.milestones"
        :key="milestone.key"
        class="milestone"
        :class="milestoneClass(milestone)">
        <div
          v-if="index > 0"
          class="connector"
          :class="milestoneClass(milestone)"></div>
        <div class="circle">
          <i :class="icons[milestone.key]"></i>
        </div>
        <div class="label">{{ $t(`key_orderProgress_${milestone.key}`) }}</div>
        <div class="date">{{ milestone.date ? $dateUtils.toDDMMYYYYHH(milestone.date) : '—' }}</div>
      </div>
    </div>

    <Modal v-if="trackingUrls.length > 1" id="orderProgressTrackingModal">
      <template #header>{{ $t('key_orderProgress_tracking_links') }}</template>
      <template #body>
        <ul class="list-unstyled mb-0">
          <li v-for="url in trackingUrls" :key="url" class="mb-2 text-sm text-break">
            <a :href="url" target="_blank" rel="noopener noreferrer">
              {{ url }}
              <i class="fa-solid fa-arrow-up-right-from-square ms-1"></i>
            </a>
          </li>
        </ul>
      </template>
      <template #footer>
        <button type="button" class="btn btn-outline-primary" data-bs-dismiss="modal">
          {{ $t('key_close') }}
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
  import Modal from "@/components/ModalHotakauth.vue";
  import { COLORS } from "@/views/dashboards/overview/shared.js";

  // Milestones that stop the progress line; the back sends them in red.
  const BLOCKING_KEYS = ["on_hold", "cancelled"];

  export default {
    name: "OrderProgress",
    components: { Modal },
    data() {
      return {
        colors: COLORS,
        icons: {
          placed: "fa-regular fa-file-lines",
          on_hold: "fa-solid fa-pause",
          cancelled: "fa-solid fa-xmark",
          processing: "fa-solid fa-gear",
          fulfilled: "fa-solid fa-box",
          shipped: "fa-solid fa-truck",
        },
      };
    },
    computed: {
      progress() {
        return this.$store.state.order.orderProgress.data;
      },
      shipment() {
        return this.progress ? this.progress.shipment : null;
      },
      courier() {
        return this.shipment ? this.shipment.courier : "";
      },
      trackingNumbers() {
        return this.shipment ? this.shipment.tracking_numbers.join(", ") : "";
      },
      trackingUrls() {
        return this.shipment ? this.shipment.tracking_urls : [];
      },
      trackLabel() {
        return this.courier
          ? this.$t("key_orderProgress_track_on", { courier: this.courier })
          : this.$t("key_orderProgress_track");
      },
    },
    methods: {
      milestoneClass(milestone) {
        if (!milestone.date) return "pending";
        return BLOCKING_KEYS.includes(milestone.key) ? "blocked" : "reached";
      },
    },
  };
</script>

<style scoped>
  .order-progress {
    padding: 1.5rem;
    border-radius: 1rem;
    background: #fff;
    box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.05);
  }

  .shipment-info strong {
    color: #344767;
  }

  .milestones {
    display: flex;
  }

  .milestone {
    position: relative;
    flex: 1;
    text-align: center;
  }

  .circle {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto;
    border-radius: 50%;
    border: 0.25rem solid #fff;
    box-shadow: 0 0 0 1px v-bind('colors.track');
    background: v-bind('colors.track');
    color: #8392ab;
    font-size: 1.1rem;
  }

  .reached .circle {
    background: v-bind('colors.green');
    box-shadow: 0 0 0 1px v-bind('colors.green');
    color: #fff;
  }

  .blocked .circle {
    background: v-bind('colors.red');
    box-shadow: 0 0 0 0.35rem v-bind('colors.redTint');
    color: #fff;
  }

  /* Joins this milestone to the previous one, behind both circles. */
  .connector {
    position: absolute;
    top: 1.65rem;
    right: 50%;
    width: 100%;
    height: 0.25rem;
    background: v-bind('colors.track');
  }

  .connector.reached {
    background: v-bind('colors.green');
  }

  .connector.blocked {
    background: v-bind('colors.red');
  }

  .label {
    margin-top: 0.75rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: #344767;
  }

  .date {
    font-size: 0.75rem;
    color: #8392ab;
  }

  .blocked .date {
    font-weight: 600;
    color: v-bind('colors.red');
  }
</style>
